import React, { useEffect, useRef } from 'react';
import { scrollState, clamp, smoothstep } from '@/lib/depth';

/**
 * Per-section CSS atmospheres. A single fixed full-screen layer (z-index 2)
 * whose radial-gradient environment morphs smoothly as scrollState.camera
 * advances through the depth map. Every zone is described by two fully
 * parameterized gradient layers (warm "amber" + cold "blue"); all numeric
 * params are linearly interpolated between adjacent zone anchors, so there
 * are no hard cuts. Story flickers; TKB breathes.
 *
 * A layer is: { w, h, x, y, c0:[r,g,b,a], s0, c1:[r,g,b,a], s1, s2 }.
 * The final stop is always transparent (alpha 0) at s2%.
 */

// Each zone anchored at the MIDPOINT of its depth range so the zone reads
// "pure" at its center and cross-fades into its neighbours at the edges.
const ZONES = [
  {
    // hero 0–1900 — amber rises from below, cold blue from above
    anchor: 950,
    base: 1,
    flicker: false,
    breathe: false,
    amber: { w: 120, h: 60, x: 50, y: 110, c0: [193, 122, 42, 0.15], s0: 0, c1: [193, 122, 42, 0.075], s1: 30, s2: 60 },
    cold: { w: 130, h: 100, x: 50, y: 0, c0: [74, 111, 165, 0.2], s0: 0, c1: [74, 111, 165, 0.1], s1: 35, s2: 70 },
  },
  {
    // story / about 1900–3900 — inside the moonlit cloud sea: a luminous cool
    // wash with only a distant warm glow from the cliff we left behind
    anchor: 2900,
    base: 1,
    flicker: true,
    breathe: false,
    amber: { w: 90, h: 50, x: 50, y: 120, c0: [193, 122, 42, 0.05], s0: 0, c1: [193, 122, 42, 0.025], s1: 25, s2: 55 },
    cold: { w: 135, h: 105, x: 50, y: 42, c0: [150, 178, 208, 0.2], s0: 0, c1: [110, 142, 180, 0.1], s1: 38, s2: 78 },
  },
  {
    // work 3900–5900 — desk lamp right, monitor blue left
    anchor: 4900,
    base: 1,
    flicker: false,
    breathe: false,
    amber: { w: 70, h: 60, x: 75, y: 60, c0: [193, 122, 42, 0.1], s0: 0, c1: [193, 122, 42, 0.05], s1: 25, s2: 50 },
    cold: { w: 90, h: 70, x: 30, y: 40, c0: [74, 111, 165, 0.15], s0: 0, c1: [74, 111, 165, 0.075], s1: 32, s2: 65 },
  },
  {
    // youtube 5900–7700 — full monitor glow, coldest stop (amber off)
    anchor: 6800,
    base: 1,
    flicker: false,
    breathe: false,
    amber: { w: 70, h: 60, x: 75, y: 60, c0: [193, 122, 42, 0], s0: 0, c1: [193, 122, 42, 0], s1: 25, s2: 50 },
    cold: { w: 100, h: 80, x: 50, y: 50, c0: [74, 111, 165, 0.22], s0: 0, c1: [74, 111, 165, 0.11], s1: 35, s2: 70 },
  },
  {
    // social 7700–9500 — city window cold above, distant amber below
    anchor: 8600,
    base: 1,
    flicker: false,
    breathe: false,
    amber: { w: 80, h: 40, x: 50, y: 110, c0: [193, 122, 42, 0.08], s0: 0, c1: [193, 122, 42, 0.04], s1: 25, s2: 50 },
    cold: { w: 100, h: 50, x: 50, y: 0, c0: [74, 111, 165, 0.18], s0: 0, c1: [74, 111, 165, 0.09], s1: 30, s2: 60 },
  },
  {
    // mediakit 9500–11200 — single tight amber spotlight from above (cold off)
    anchor: 10350,
    base: 1,
    flicker: false,
    breathe: false,
    amber: { w: 40, h: 60, x: 50, y: 0, c0: [193, 122, 42, 0.12], s0: 0, c1: [193, 122, 42, 0.06], s1: 25, s2: 50 },
    cold: { w: 100, h: 50, x: 50, y: 0, c0: [74, 111, 165, 0], s0: 0, c1: [74, 111, 165, 0], s1: 30, s2: 60 },
  },
  {
    // threshold 11200–13100 — amber rises sharply, cold blue retreats
    anchor: 12150,
    base: 1,
    flicker: false,
    breathe: false,
    amber: { w: 120, h: 70, x: 50, y: 120, c0: [193, 122, 42, 0.35], s0: 0, c1: [120, 30, 30, 0.2], s1: 40, s2: 70 },
    cold: { w: 120, h: 70, x: 50, y: 0, c0: [74, 111, 165, 0], s0: 0, c1: [74, 111, 165, 0], s1: 30, s2: 60 },
  },
  {
    // tkb 13100–15200 — deep ember from below, breathing pulse
    anchor: 14150,
    base: 1,
    flicker: false,
    breathe: true,
    amber: { w: 140, h: 60, x: 50, y: 120, c0: [120, 30, 30, 0.4], s0: 0, c1: [193, 122, 42, 0.2], s1: 30, s2: 65 },
    cold: { w: 140, h: 60, x: 50, y: 0, c0: [74, 111, 165, 0], s0: 0, c1: [74, 111, 165, 0], s1: 30, s2: 60 },
  },
  {
    // letters/end 15200–17000 — same as TKB but amber more distant, darker
    anchor: 16100,
    base: 0.7,
    flicker: false,
    breathe: false,
    amber: { w: 140, h: 60, x: 50, y: 130, c0: [120, 30, 30, 0.3], s0: 0, c1: [193, 122, 42, 0.14], s1: 30, s2: 65 },
    cold: { w: 140, h: 60, x: 50, y: 0, c0: [74, 111, 165, 0], s0: 0, c1: [74, 111, 165, 0], s1: 30, s2: 60 },
  },
];

const lerp = (a, b, t) => a + (b - a) * t;
const lerpColor = (a, b, t) => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t), lerp(a[3], b[3], t)];

const lerpLayer = (a, b, t) => ({
  w: lerp(a.w, b.w, t),
  h: lerp(a.h, b.h, t),
  x: lerp(a.x, b.x, t),
  y: lerp(a.y, b.y, t),
  c0: lerpColor(a.c0, b.c0, t),
  s0: lerp(a.s0, b.s0, t),
  c1: lerpColor(a.c1, b.c1, t),
  s1: lerp(a.s1, b.s1, t),
  s2: lerp(a.s2, b.s2, t),
});

const rgba = (c, override) => `rgba(${Math.round(c[0])}, ${Math.round(c[1])}, ${Math.round(c[2])}, ${(override !== undefined ? override : c[3]).toFixed(3)})`;

const gradient = (l, amberAlphaBoost) => {
  const c0 = amberAlphaBoost ? [l.c0[0], l.c0[1], l.c0[2], clamp(l.c0[3] + amberAlphaBoost, 0, 1)] : l.c0;
  const c1 = amberAlphaBoost ? [l.c1[0], l.c1[1], l.c1[2], clamp(l.c1[3] + amberAlphaBoost, 0, 1)] : l.c1;
  return `radial-gradient(ellipse ${l.w.toFixed(1)}% ${l.h.toFixed(1)}% at ${l.x.toFixed(1)}% ${l.y.toFixed(1)}%, ${rgba(c0)} ${l.s0.toFixed(1)}%, ${rgba(c1)} ${l.s1.toFixed(1)}%, ${rgba(l.c1, 0)} ${l.s2.toFixed(1)}%)`;
};

export const AmbientWorld = () => {
  const ref = useRef(null);

  useEffect(() => {
    let raf;
    let t = 0;
    let flickerOffset = 0;
    let nextFlicker = 1.5 + Math.random() * 1.5;

    const tick = () => {
      t += 0.016;
      const cam = scrollState.camera;

      // Flicker: re-roll a subtle ±0.04 nudge every 2.5–4s.
      if (t > nextFlicker) {
        flickerOffset = (Math.random() - 0.5) * 0.08;
        nextFlicker = t + 2.5 + Math.random() * 1.5;
      }

      // Find the segment between two zone anchors and interpolate.
      let lo = ZONES[0];
      let hi = ZONES[0];
      let f = 0;
      if (cam <= ZONES[0].anchor) {
        lo = hi = ZONES[0];
      } else if (cam >= ZONES[ZONES.length - 1].anchor) {
        lo = hi = ZONES[ZONES.length - 1];
      } else {
        for (let i = 0; i < ZONES.length - 1; i += 1) {
          if (cam >= ZONES[i].anchor && cam <= ZONES[i + 1].anchor) {
            lo = ZONES[i];
            hi = ZONES[i + 1];
            f = (cam - lo.anchor) / (hi.anchor - lo.anchor);
            break;
          }
        }
      }

      const amber = lerpLayer(lo.amber, hi.amber, f);
      const cold = lerpLayer(lo.cold, hi.cold, f);
      const base = lerp(lo.base, hi.base, f);

      // Story flicker — only nudge amber while inside the story range.
      const flickerWeight = smoothstep(1900, 2100, cam) * (1 - smoothstep(3700, 3900, cam));
      const amberBoost = flickerOffset * flickerWeight;

      // TKB breathing pulse — opacity 0.85–1.0, 4s ease-in-out, only in tkb range.
      const breatheWeight = smoothstep(13100, 13400, cam) * (1 - smoothstep(14900, 15200, cam));
      const breathe = 0.925 + 0.075 * Math.sin((t / 4) * Math.PI * 2);
      const opacity = base * lerp(1, breathe, breatheWeight);

      if (ref.current) {
        ref.current.style.background = `${gradient(amber, amberBoost)}, ${gradient(cold)}`;
        ref.current.style.opacity = opacity.toFixed(3);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 2 }}
      aria-hidden="true"
      data-testid="ambient-world"
    >
      <div ref={ref} className="absolute inset-0" />
    </div>
  );
};

export default AmbientWorld;
